/**
 * Pure helpers for client-side insight strips (donut + metric cards).
 * All inputs are rows already loaded by a list page's query.
 */

/** Group rows by keyFn; null/undefined/'' keys land in the '-' bucket. */
export function countBy(rows, keyFn) {
  const out = {};
  for (const row of rows || []) {
    const key = keyFn(row) || '-';
    out[key] = (out[key] || 0) + 1;
  }
  return out;
}

/** Sum numeric (or numeric-string) values; NaN counts as 0. Optional row filter. */
export function sumBy(rows, valueFn, filterFn) {
  let total = 0;
  for (const row of rows || []) {
    if (filterFn && !filterFn(row)) continue;
    const n = Number(valueFn(row));
    if (Number.isFinite(n)) total += n;
  }
  return total;
}

/** Integer percentage with division-by-zero guard. */
export function percent(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

/**
 * Donut-friendly buckets from a countBy result: named buckets sorted by count
 * (descending); beyond `max` buckets only the `top` largest stay and the tail
 * is folded into the trailing '-' bucket. Returns `[key, count]` entries.
 */
export function topBuckets(counts, { max = 6, top = 5 } = {}) {
  const named = Object.entries(counts || {})
    .filter(([key]) => key !== '-')
    .sort((a, b) => b[1] - a[1]);
  const dashCount = (counts || {})['-'] || 0;
  if (named.length + (dashCount ? 1 : 0) <= max) {
    return dashCount ? [...named, ['-', dashCount]] : named;
  }
  const kept = named.slice(0, top);
  const folded = named.slice(top).reduce((sum, [, n]) => sum + n, 0) + dashCount;
  return folded > 0 ? [...kept, ['-', folded]] : kept;
}
